import type { DataProvider, I18nProvider } from '@refinedev/core';
import type { AxiosError, AxiosInstance } from 'axios';
import queryString from 'query-string';

import { axiosInstance, generateFilter, generateSort } from './utils';
import { ErrorReponseType, getErrorMessage } from './utils/error-helper';

type MethodTypes = 'get' | 'delete' | 'head' | 'options';

type MethodTypesWithBody = 'post' | 'put' | 'patch';

export const restDataProvider = (
  apiUrl: string,
  i18nProvider: I18nProvider,
  httpClient: AxiosInstance = axiosInstance,
): Omit<Required<DataProvider>, 'createMany' | 'updateMany' | 'deleteMany'> => ({
  getList: async ({ resource, pagination, filters, sorters, meta }) => {
    const url = `${apiUrl}/${resource}`;

    const { currentPage = 1, pageSize = 10, mode = 'server' } = pagination ?? {};

    const { headers: headersFromMeta, method } = meta ?? {};
    const requestMethod = (method as MethodTypes) ?? 'get';

    const queryFilters = generateFilter(filters, resource);

    const query: {
      size?: number;
      page?: number;
      _sort?: string;
      _order?: string;
    } = {};

    if (mode === 'server') {
      query.size = pageSize;
      query.page = currentPage;
    }

    const generatedSort = generateSort(sorters);
    if (generatedSort) {
      const { _sort, _order } = generatedSort;
      query._sort = _sort.join(',');
      query._order = _order.join(',');
    }

    const combinedQuery = { ...query, ...queryFilters };
    const urlWithQuery = Object.keys(combinedQuery).length
      ? `${url}?${queryString.stringify(combinedQuery)}`
      : url;

    try {
      const { data } = await httpClient[requestMethod](urlWithQuery, {
        headers: headersFromMeta,
      });

      return {
        data: data.items,
        total: data?.meta?.totalCount,
      };
    } catch (e: unknown) {
      const message = getErrorMessage(i18nProvider, e as AxiosError<ErrorReponseType>);
      throw { ...(e as AxiosError<ErrorReponseType>), message };
    }
  },

  getMany: async ({ resource, ids, meta }) => {
    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypes) ?? 'get';

    try {
      const { data } = await httpClient[requestMethod](
        `${apiUrl}/${resource}?${queryString.stringify({ id: ids })}`,
        { headers },
      );

      return {
        data,
      };
    } catch (e: unknown) {
      const message = getErrorMessage(i18nProvider, e as AxiosError<ErrorReponseType>);
      throw { ...(e as AxiosError<ErrorReponseType>), message };
    }
  },

  create: async ({ resource, variables, meta }) => {
    const url = `${apiUrl}/${resource}`;

    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypesWithBody) ?? 'post';

    try {
      const { data } = await httpClient[requestMethod](url, variables, {
        headers,
      });

      return {
        data,
      };
    } catch (e: unknown) {
      const message = getErrorMessage(i18nProvider, e as AxiosError<ErrorReponseType>);
      throw { ...(e as AxiosError<ErrorReponseType>), message };
    }
  },

  update: async ({ resource, id, variables, meta }) => {
    const url = `${apiUrl}/${resource}/${id}`;

    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypesWithBody) ?? 'put';

    try {
      const { data } = await httpClient[requestMethod](url, variables, {
        headers,
      });

      return {
        data,
      };
    } catch (e: unknown) {
      const message = getErrorMessage(i18nProvider, e as AxiosError<ErrorReponseType>);
      throw { ...(e as AxiosError<ErrorReponseType>), message };
    }
  },

  getOne: async ({ resource, id, meta }) => {
    const url = `${apiUrl}/${resource}/${id}`;

    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypes) ?? 'get';

    try {
      const { data } = await httpClient[requestMethod](url, { headers });

      return {
        data,
      };
    } catch (e: unknown) {
      const message = getErrorMessage(i18nProvider, e as AxiosError<ErrorReponseType>);
      throw { ...(e as AxiosError<ErrorReponseType>), message };
    }
  },

  deleteOne: async ({ resource, id, variables, meta }) => {
    const url = `${apiUrl}/${resource}/${id}`;

    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypesWithBody) ?? 'delete';

    try {
      const { data } = await httpClient[requestMethod](url, {
        data: variables,
        headers,
      });

      return {
        data,
      };
    } catch (e: unknown) {
      const message = getErrorMessage(i18nProvider, e as AxiosError<ErrorReponseType>);
      throw { ...(e as AxiosError<ErrorReponseType>), message };
    }
  },

  getApiUrl: () => {
    return apiUrl;
  },

  custom: async ({ url, method, filters, sorters, payload, query, headers }) => {
    let requestUrl = `${url}?`;

    if (sorters) {
      const generatedSort = generateSort(sorters);
      if (generatedSort) {
        const { _sort, _order } = generatedSort;
        const sortQuery = {
          _sort: _sort.join(','),
          _order: _order.join(','),
        };

        requestUrl = `${requestUrl}&${queryString.stringify(sortQuery)}`;
      }
    }

    if (filters) {
      const filterQuery = generateFilter(filters);
      requestUrl = `${requestUrl}&${queryString.stringify(filterQuery)}`;
    }

    if (query) {
      requestUrl = `${requestUrl}&${queryString.stringify(query)}`;
    }

    let axiosResponse;
    try {
      switch (method) {
        case 'put':
        case 'post':
        case 'patch':
          axiosResponse = await httpClient[method](url, payload, {
            headers,
          });
          break;
        case 'delete':
          axiosResponse = await httpClient.delete(url, {
            data: payload,
            headers: headers,
          });
          break;
        default:
          axiosResponse = await httpClient.get(requestUrl, {
            headers,
          });
          break;
      }

      const { data } = axiosResponse;

      return Promise.resolve({ data });
    } catch (e) {
      const message = getErrorMessage(i18nProvider, e as AxiosError<ErrorReponseType>);
      throw { ...(e as AxiosError<ErrorReponseType>), message };
    }
  },
});
